import { ActivitySpecificQuestions, useWizard } from '@insify/funnel-components';
import { isDefined } from '@insify/ui-utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { additionalQuestionKeys } from '../../../util/activitySpecificQuestions';
import { useCommands } from '../../hooks/useCommands';
import { useContent } from '../../hooks/useContent';
import { useConditionsQuery, useParamsQuery } from '../../hooks/useQueries';

import type { ActivitySpecificQuestionsFormType } from '@insify/funnel-components';
import type { SubmitHandler } from 'react-hook-form';

export function AdditionalQuestionsPage(): JSX.Element | null {
  const { showPreviousPage, showNextPage } = useWizard();
  const { t } = useTranslation(['common', 'funnel']);
  const { quoteWizardAdditionalQuestions } = useContent() || {};
  const { sendCommand } = useCommands();
  const { isStale, data: quoteParams, isFetched } = useParamsQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [triggerShowingNextPage, setTriggerShowingNextPage] = useState(false);
  const { data: quoteConditions } = useConditionsQuery();
  const questions = additionalQuestionKeys
    .map((key) => {
      return quoteConditions?.activitySpecificFieldDescriptions?.find(({ key: k }) => k === key);
    })
    .filter(isDefined);

  const handleCommand: SubmitHandler<ActivitySpecificQuestionsFormType> = async ({ activitySpecificFieldAnswers }) => {
    setIsSubmitting(true);
    await sendCommand({
      type: 'confirmActivitySpecificFields',
      payload: activitySpecificFieldAnswers,
    });
    setTriggerShowingNextPage(true);
  };

  // Ensure that the additional questions form data is saved on the backend
  const ready =
    triggerShowingNextPage &&
    !isStale &&
    questions.some(({ key }) => key in (quoteParams?.activitySpecificFieldAnswers || {}));

  useEffect(() => {
    if (ready) {
      showNextPage();
      setTriggerShowingNextPage(false);
    }
  }, [ready]);

  if (!isFetched || !quoteParams) {
    return null;
  }

  return (
    <ActivitySpecificQuestions
      formData={quoteParams}
      questions={questions}
      eventHandlers={{
        onSubmit: handleCommand,
        onBack: showPreviousPage,
      }}
      content={quoteWizardAdditionalQuestions}
      translations={{
        back: t('common:back'),
        forward: t('common:continue'),
        readMore: t('funnel:activity_specific_questions_read_more'),
      }}
      submitting={isSubmitting}
    />
  );
}
